import React from 'react'
import {BoxProps, HStack, Text, useMultiStyleConfig} from '@chakra-ui/react'
import {Button, ColorCircle, Icon} from '_uiComponents'
import {ProductVariationGroup} from '_types'
import {useIntl} from 'react-intl'
import {useViewport} from '_customHooks'

interface ProductColorSwatchesProps {
    variationGroups: Array<ProductVariationGroup>
    selectedColorCode?: string
    onSwatchClick: (colorCode: string, variationGroup: ProductVariationGroup) => void
    onMoreClick: () => void
}

/**
 * @name ProductColorSwatches
 *
 * @component
 *
 * @description
 * Renders an inline colour selector row for the PDP. 
 * Shows "Colore: {label}" on the left and up to MAX_VISIBLE_SWATCHES circular
 * swatches on the right, filled with hex colours from `c_colorHexCodes`.
 * If there are more colours a "+ N colori" CTA opens the full drawer.
 */
const ProductColorSwatches: React.FC<ProductColorSwatchesProps> = ({
    variationGroups,
    selectedColorCode,
    onSwatchClick,
    onMoreClick,
}: ProductColorSwatchesProps) => {
    const {formatMessage} = useIntl()
    const {isMobile} = useViewport()
    const maxVisibleSwatches = isMobile ? 3 : 6

    const selectableGroups = variationGroups?.filter((vg) => vg.selectable !== false || (vg.online === true && vg.notifyMeEnabled === true)) ?? []
    const visibleGroups = selectableGroups.slice(0, maxVisibleSwatches)
    const hiddenCount = Math.max(0, selectableGroups.length - maxVisibleSwatches)
    const styles = useMultiStyleConfig('ProductColorSelector', {moreColor: hiddenCount}) as Record<string, BoxProps>

    const selectedLabel =
        variationGroups?.find((vg) => vg.color.code === selectedColorCode)?.color?.label ?? variationGroups?.find((vg) => vg.default)?.color?.label

    const LABEL_COLOR = formatMessage({id: 'label.pdp.color', defaultMessage: 'Color'})
    const LABEL_ONEMORE_COLOR = formatMessage({id: 'label.pdp.more.color', defaultMessage: '+ {0} color'}, {0: hiddenCount})
    const LABEL_MORE_COLORS = formatMessage({id: 'label.pdp.more.colors', defaultMessage: '+ {0} colors'}, {0: hiddenCount})

    return (
        <HStack {...styles.swatchesRow}>
            <Text {...styles.swatchesLabel}>
                {LABEL_COLOR}: {selectedLabel}
            </Text>
            <HStack {...styles.swatchesList}>
                {visibleGroups.map((vg, index) => {
                    const isSelected = vg.color.code === selectedColorCode
                    const hexCodes = vg.color.colorHexCode ?? null

                    return (
                        <Button
                            key={`swatch-${vg.ID ?? index}`}
                            variant="round-swatch"
                            onClick={() => onSwatchClick(vg.color.code!, vg)}
                            aria-label={vg.color.label}
                            aria-pressed={isSelected}
                        >
                            <ColorCircle hexCodes={hexCodes} selected={isSelected} />
                        </Button>
                    )
                })}
                {hiddenCount > 0 &&
                    (isMobile ? (
                        // Mobile: show only a "+" icon. The "+ N colour(s)" copy is kept as the button's
                        // accessible name (aria-label) so screen reader / voice-control users still hear the
                        // count, while the icon is decorative and hidden from the accessibility tree
                        // (a11y: icon-only button → aria-label on the control, aria-hidden on the icon).
                        <Button
                            sx={styles.swatchMoreIconCta}
                            variant="text"
                            onClick={onMoreClick}
                            aria-label={hiddenCount === 1 ? LABEL_ONEMORE_COLOR : LABEL_MORE_COLORS}
                        >
                            {/* Figma 58120-18452: Plus-Light (stroke) icon at 24px, no underline. */}
                            <Icon name="IconPlusLight" size="md" aria-hidden={true} />
                        </Button>
                    ) : (
                        <Button sx={styles.swatchMoreCta} variant="textUnderline" onClick={onMoreClick}>
                            {hiddenCount === 1 ? LABEL_ONEMORE_COLOR : LABEL_MORE_COLORS}
                        </Button>
                    ))}
            </HStack>
        </HStack>
    )
}

export default ProductColorSwatches
